import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthApiError, authRequest } from '../../core/api/authApiClient';

type DonationHistoryResponse = {
  statusCode?: number;
  resultCode?: number;
  result_Code?: number;
  resultMessage?: string;
  result_message?: string;
  message?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isNotFoundError = (statusCode?: number, resultCode?: number, message?: string) => {
  if (statusCode === 404 || resultCode === 404) return true;
  if (!message) return false;
  return /존재하지 않는 이메일|존재하지 않는 계정|incorrect email|not found|user not found/i.test(message);
};

const toNumberOrUndefined = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const useDonationHistoryFind = () => {
  const { i18n, t } = useTranslation('translation');
  const [email, setEmailState] = useState('');
  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const language = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isEmailFormatInvalid = useMemo(
    () => Boolean(trimmedEmail) && !EMAIL_REGEX.test(trimmedEmail),
    [trimmedEmail],
  );
  const displayError = isEmailFormatInvalid ? t('page.passwordFind.errors.invalidEmailFormat') : apiError;
  const isSubmitDisabled = isSubmitting || !trimmedEmail || isEmailFormatInvalid;

  const setEmail = useCallback((value: string) => {
    setEmailState(value);
    setApiError(undefined);
    setIsSuccessModalOpen(false);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const targetEmail = trimmedEmail;
    if (!EMAIL_REGEX.test(targetEmail)) {
      setApiError(undefined);
      setIsSuccessModalOpen(false);
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError(undefined);
      setIsSuccessModalOpen(false);

      const response = await authRequest<DonationHistoryResponse>('/donors/mydonation', {
        method: 'POST',
        body: {
          user_email: targetEmail,
          language,
        },
      });

      const statusCode = toNumberOrUndefined(response?.statusCode);
      const resultCode = toNumberOrUndefined(response?.resultCode ?? response?.result_Code);
      const resultMessage = String(response?.resultMessage ?? response?.result_message ?? response?.message ?? '');

      if (resultCode === 10001) {
        setApiError(resultMessage || t('page.passwordFind.errors.default'));
        return;
      }

      if (isNotFoundError(statusCode, resultCode, resultMessage)) {
        setApiError(t('page.passwordFind.errors.emailNotFound'));
        return;
      }

      const isSuccessResponse = statusCode === 200 && resultCode === 0;
      if (!isSuccessResponse) {
        throw new Error(resultMessage || t('page.passwordFind.errors.default'));
      }

      setSentEmail(targetEmail);
      setIsSuccessModalOpen(true);
    } catch (error) {
      if (error instanceof AuthApiError) {
        const errorBody = error.body as {
          message?: string;
          result_message?: string;
          resultMessage?: string;
          statusCode?: number;
          resultCode?: number;
        } | undefined;
        const bodyMessage = errorBody?.resultMessage ?? errorBody?.result_message ?? errorBody?.message;
        if (isNotFoundError(error.status ?? errorBody?.statusCode, errorBody?.resultCode, bodyMessage)) {
          setApiError(t('page.passwordFind.errors.emailNotFound'));
          return;
        }
      }

      const errorMessage = error instanceof Error ? error.message : '';
      if (isNotFoundError(undefined, undefined, errorMessage)) {
        setApiError(t('page.passwordFind.errors.emailNotFound'));
        return;
      }

      setApiError(t('page.passwordFind.errors.default'));
    } finally {
      setIsSubmitting(false);
    }
  }, [language, t, trimmedEmail]);

  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false);
  }, []);

  return {
    email,
    setEmail,
    error: displayError,
    isSubmitting,
    isSubmitDisabled,
    isSuccessModalOpen,
    sentEmail,
    closeSuccessModal,
    handleSubmit,
    language,
  };
};

